import React from "react";
import { Link } from "react-router-dom";
import { Phone, Check, Camera, FileText, CloudLightning, ShieldAlert, ArrowRight } from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { PageHero } from "./Services";
import { brand, afterRoof, inspection, insuranceCarriers } from "../data/site";
import BrandLogo from "../components/BrandLogo";

const helps = [
  { icon: Camera, title: "Photo-Documented Inspection", text: "We get on the roof and photograph every area of damage, plus the flashing, penetrations and underlayment underneath." },
  { icon: FileText, title: "Documentation For Your Carrier", text: "A written scope with line-item pricing and photos your insurer can actually work from, instead of a one-line estimate." },
  { icon: CloudLightning, title: "Monsoon, Wind & Hail Damage", text: "We know what Arizona storms do to tile, shingle and foam roofs, and what an adjuster is looking for." },
  { icon: ShieldAlert, title: "After a Denial", text: "A denial isn't always the end. We'll look at the roof, tell you what we see, and explain the options that remain." },
];

const faqs = [
  {
    q: "My claim was denied. Is there anything left to do?",
    a: "Sometimes. A denial often comes down to what was documented. We inspect the roof, photograph what we find, and give you a written assessment. Whether that changes the outcome is up to your carrier, but you'll at least know the true condition of your roof.",
  },
  {
    q: "Do you handle the claim for me?",
    a: "No. We're a roofing contractor, not a public adjuster or law firm. We document the roof and provide a professional scope and pricing. You (or a licensed public adjuster) deal with the carrier.",
  },
  {
    q: "What does the inspection cost?",
    a: "Nothing. Roof inspections are free, whether or not there's a claim involved, and you get the photos either way.",
  },
  {
    q: "Should I file a claim for every leak?",
    a: "Not necessarily. Many repairs cost less than a deductible, and claims history can affect your premium. We'll tell you honestly when a repair is the better route.",
  },
  {
    q: "What if the damage happened months ago?",
    a: "Policies have reporting deadlines that vary. Get the roof looked at and documented now, then check your policy or ask your agent about the timeline that applies.",
  },
];

const InsuranceClaims = () => (
  <div>
    <SEO
      title="Roof Insurance Claim Help in Phoenix, AZ | Pepla Roofing"
      description="Storm damage roof inspections with photo documentation for your insurance carrier across Maricopa County. Claim denied? We'll take a look. Call (602) 834-4707."
      jsonLd={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            "@id": `${SITE_URL}/insurance-claims#service`,
            name: "Roof insurance claim documentation and storm damage inspection",
            serviceType: "Storm damage roof inspection",
            provider: { "@id": `${SITE_URL}/#business` },
            areaServed: { "@type": "AdministrativeArea", name: "Maricopa County, Arizona" },
            url: `${SITE_URL}/insurance-claims`,
          },
          {
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      }}
    />

    <PageHero
      eyebrow="Storm & Insurance"
      title="Claim Denied? Don't Stop There."
      subtitle="We inspect the roof, document the damage with photos, and give you a written scope your carrier can work from."
      image={afterRoof}
      imageAlt="Arizona tile roof after storm damage repair by Pepla Roofing"
    />

    {/* Carriers we've worked claims against.
        These are not partners and the disclaimer under the row says so in
        print. Pepla is an independent contractor; implying an insurer has
        endorsed or approved us would be both untrue and a problem. */}
    <section className="pt-20 pb-16 bg-secondary border-b border-ink/10">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="kicker">Familiar Paperwork</span>
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-3 mb-4">
            Carriers We Work With Regularly
          </h2>
          <div className="brand-line mb-5" />
          <p className="text-muted-foreground">
            We've documented roof claims for Valley homeowners insured by most of the major carriers. Knowing how each
            one scopes a roof — and what its adjusters want to see — is why our photo reports and line-item scopes
            usually need fewer rounds.
          </p>
        </div>

        {/* Flex rather than grid, because the carrier count is not a multiple
            of the column count and a grid leaves the last row hanging to the
            left with a hole on the right. `justify-center` centers a partial
            row. The basis values are the column width minus that breakpoint's
            share of the gaps, so full rows still line up like a grid. */}
        <ul
          className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-9 [--logo-h:2.1rem] md:gap-x-8 md:[--logo-h:2.3rem] lg:gap-x-8 lg:[--logo-h:2.4rem] xl:gap-x-12 xl:[--logo-h:2.5rem]"
          aria-label="Insurance carriers whose claims we have worked on"
        >
          {insuranceCarriers.map((c) => (
            <li
              key={c.file}
              // Five across from `md` up, which is what holds the strip to
              // three rows on a laptop-width window. Two and three across
              // below that — five logos on a phone would be 60px each.
              className="flex items-center justify-center min-w-0 grow-0 shrink-0 basis-[calc(50%-0.75rem)] sm:basis-[calc(33.333%-1rem)] md:basis-[calc(20%-1.6rem)] xl:basis-[calc(20%-2.4rem)]"
            >
              <BrandLogo brand={c} />
            </li>
          ))}
        </ul>

        <p className="text-xs text-ink/45 mt-10 leading-relaxed">
          Pepla Roofing is an independent licensed Arizona roofing contractor. We are not affiliated with, endorsed by,
          sponsored by or an approved vendor of any insurance company, and we do not act as a public adjuster. Carrier
          names and logos are the property of their respective owners and appear here only to identify companies whose
          claims we have worked on. Your policy and your carrier decide what is covered.
        </p>
      </div>
    </section>

    {/* What we do */}
    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl mb-14">
          <span className="kicker">How We Help</span>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
            The Roofing Side of Your Claim
          </h2>
          <div className="brand-line mb-5" />
          <p className="text-muted-foreground text-lg">
            Monsoon wind, hail and blowing debris do real damage to Valley roofs, and a lot of it isn't visible from
            the ground. Whether you're about to file, waiting on an adjuster, or already have a denial letter, the
            first step is the same: get the roof looked at and documented properly.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {helps.map((h) => (
            <div key={h.title} className="card-lift bg-card border border-ink/10 rounded-2xl p-7">
              <h.icon size={28} className="text-brand mb-4" />
              <h3 className="font-heading text-lg uppercase tracking-wide mb-2">{h.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{h.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-secondary border border-ink/10 rounded-2xl p-7">
          <p className="text-sm leading-relaxed text-ink/80">
            <strong className="font-semibold">What we are, and what we aren't.</strong> {brand.name} is a roofing
            contractor. We are not a public adjuster, an attorney, or an insurance company. We don't negotiate claims
            on your behalf and can't promise a claim will be approved, covered, or paid at any particular amount. What
            we can do is inspect your roof, document what's there, and price the work honestly.
          </p>
        </div>
      </div>
    </section>

    {/* Steps + form */}
    <section className="py-24 section-dark text-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12">
        <div>
          <span className="kicker-dark">What To Expect</span>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Start With the Roof</h2>
          <div className="brand-line mb-7" />
          <ul className="space-y-5">
            {[
              "Call or send the form. Tell us roughly when the damage happened — we call back to confirm a time.",
              inspection.sameDay
                ? `We inspect ${inspection.hours} — often the same day, depending on crew routes.`
                : "We schedule the inspection as soon as our crews are in your area.",
              "We photograph the roof and write up what we find, with pricing.",
              "You decide whether to file, repair out of pocket, or wait.",
              "If repairs go ahead, we schedule the work and document the finished job.",
            ].map((t) => (
              <li key={t} className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={14} className="text-brand" />
                </span>
                <span className="text-white/75 leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <a
            href={`tel:${brand.phoneRaw}`}
            className="inline-flex items-center gap-3 mt-10 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
          >
            <Phone size={18} /> {brand.phone}
          </a>
        </div>

        <div id="quote" className="bg-white/5 border border-white/10 rounded-2xl p-8">
          <h2 className="font-heading text-2xl uppercase tracking-wide mb-2">Request a Storm Inspection</h2>
          <p className="text-white/60 text-sm mb-6">Free, no obligation, and you keep the photos either way.</p>
          <LeadForm dark defaultService="Roofing" />
        </div>
      </div>
    </section>

    {/* FAQs */}
    <section className="py-24 bg-secondary">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <span className="kicker">Answers</span>
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Claim Questions</h2>
          <div className="brand-line mx-auto" />
        </div>
        <div>
          {faqs.map((f) => (
            <div key={f.q} className="py-6 border-b border-ink/10 first:border-t first:border-ink/10">
              <h3 className="font-heading text-lg uppercase tracking-wide mb-2">{f.q}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/services/roofing"
            className="inline-flex items-center gap-2 border border-ink/20 hover:border-brand hover:text-brand font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
          >
            See Roofing Services <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default InsuranceClaims;
